/**
 * Helpers para manejo de roles y permisos
 */

/**
 * Obtiene los nombres de los roles del usuario
 */
export const getUserRoles = (user) => {
  if (!user || !user.roles) return [];
  return user.roles.map(role => role.name);
};

/** Rol de academia real. is_superuser no cuenta. */
export const canManagePerformance = (user) => {
  const roles = getUserRoles(user);
  return roles.includes('owner') || roles.includes('admin');
};

/**
 * Verifica si el usuario tiene un rol específico
 */
export const hasRole = (user, roleName) => {
  const roles = getUserRoles(user);
  return roles.includes(roleName) || user?.is_superuser;
};

/**
 * Verifica si el usuario tiene alguno de los roles especificados
 */
export const hasAnyRole = (user, ...roleNames) => {
  if (user?.is_superuser) return true;
  const roles = getUserRoles(user);
  return roleNames.some(roleName => roles.includes(roleName));
};

/**
 * Verifica si el usuario es proveedor publicitario
 */
export const isProvider = (user) => {
  return hasRole(user, 'provider');
};

/**
 * Verifica si el usuario es solo proveedor (sin otros roles de academia)
 */
export const isProviderOnly = (user) => {
  if (!isProvider(user) || user?.is_superuser) return false;
  const roles = getUserRoles(user);
  const academyRoles = ['owner', 'admin', 'teacher', 'secretary', 'student'];
  return !roles.some((r) => academyRoles.includes(r));
};

/**
 * Verifica si el usuario es superadmin
 */
export const isSuperAdmin = (user) => {
  return user?.is_superuser === true || hasRole(user, 'superadmin');
};

/**
 * Verifica si el usuario es dueño o administrador
 */
export const isOwnerOrAdmin = (user) => {
  return hasAnyRole(user, 'owner', 'admin') || isSuperAdmin(user);
};

/**
 * Verifica si el usuario es profesor
 */
export const isTeacher = (user) => {
  return hasRole(user, 'teacher');
};

/**
 * Verifica si el usuario es secretario
 */
export const isSecretary = (user) => {
  return hasRole(user, 'secretary');
};

/**
 * Obtiene el rol principal del usuario
 */
export const getPrimaryRole = (user) => {
  if (!user) return null;
  
  if (isSuperAdmin(user)) return 'superadmin';
  if (hasRole(user, 'provider')) return 'provider';
  if (hasRole(user, 'owner')) return 'owner';
  if (hasRole(user, 'admin')) return 'admin';
  if (isSecretary(user)) return 'secretary';
  if (isTeacher(user)) return 'teacher';
  
  return 'user';
};

/**
 * Obtiene el nombre del rol para mostrar
 */
export const getRoleDisplayName = (user) => {
  const role = getPrimaryRole(user);
  const roleNames = {
    'superadmin': 'Super Administrador',
    'provider': 'Proveedor publicitario',
    'owner': 'Dueño del Negocio',
    'admin': 'Administrador',
    'secretary': 'Secretario/a',
    'teacher': 'Profesor/a',
    'user': 'Usuario'
  };
  return roleNames[role] || 'Usuario';
};

